import React from "react";
import { Link, useLocation } from "react-router-dom";
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const pathTitles = {
    myaccount: "My Account",
    profile: "Profile",
    settings: "Settings",
    medicalappointments: "Medical Appointments",
    healthandfitness: "Health And Fitness",
    goalsetting: "Goal Setting",
    organizefinance: "Organize Finance",
    accountoverview: "Account Overview",
    organizeclasses: "Organize Classes",
    classschedules: 'Class Schedules',
    extraclasses: 'Extra Classes',
    studygroup: 'Study Group',
    campusandcommunity: 'Campus and Community',
    volunteeroppertunities: 'Volunteer Opportunities', // Fixed typo
    clubs: 'Clubs',
    myprofile: 'My Profile',
    todolist: 'To-Do Lists',
    quicklinks: 'Quick Links',
    studenttools: 'Student Tools',
    mylisting: 'My Listing',
    adsdetails: 'Listing details',
    favorite: 'Wishlist',
    favorites: 'Favorites',
    events: 'Events',
    participants: 'Participants',
    edit: 'Edit',
    planner: 'My Planner'
};

const Breadcrumbs = ({ dynamicTitles = {} }) => {
    const location = useLocation();
    const pathnames = location.pathname.split("/").filter(x => x);
    const isHomePage = location.pathname === "/home";
    
    const getSegmentTitle = (segment, path, pathnames) => {
        // Check if dynamic title is provided for this specific path
        if (dynamicTitles[path]) {
            return dynamicTitles[path];
        }
        
        // Check if it's a predefined title
        if (pathTitles[segment]) {
            return pathTitles[segment];
        }
        
        // Special case for favorites details
        if (pathnames.includes('favorites') && segment === 'details') {
            return 'Favorites Details';
        }
        
        // Check if it's a numeric ID (like marketplace item ID)
        if (/^\d+$/.test(segment)) {
            // Find the parent segment to determine context
            const segmentIndex = pathnames.indexOf(segment);
            const parentSegment = segmentIndex > 0 ? pathnames[segmentIndex - 1] : null;
            
            // Skip numeric IDs for these contexts
            if (parentSegment === 'adsdetails' || 
                parentSegment === 'mylisting' || 
                parentSegment === 'volunteeroppertunities' ||
                parentSegment === 'opportunities' ||
                parentSegment === 'edit') {
                return null;
            }
            
            // Handle different contexts for numeric IDs
            if (parentSegment === 'users' || parentSegment === 'user') {
                return dynamicTitles[path] || 'User Profile';
            } else if (parentSegment === 'orders' || parentSegment === 'order') {
                return dynamicTitles[path] || 'Order Details';
            } else if (parentSegment === 'posts' || parentSegment === 'post') {
                return dynamicTitles[path] || 'Post Details';
            } else {
                return dynamicTitles[path] || 'Marketplace Details';
            }
        }
        
        // Default: capitalize and replace hyphens/underscores with spaces
        return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/[-_]/g, ' ');
    };
    
    // Special handling for different path types
    let breadcrumbs;
    
    // Special handling for events participants path
    if (pathnames.includes('events') && pathnames.includes('participants')) {
        breadcrumbs = [
            { segment: 'campusandcommunity', path: '/campusandcommunity', title: 'Campus and Community' },
            { segment: 'events', path: '/campusandcommunity/events', title: 'Events' },
            { segment: 'participants', path: location.pathname, title: 'Participants' }
        ];
    } else if (pathnames[0] === 'marketplace' && pathnames.length > 2) {
        // Special handling for marketplace paths
        breadcrumbs = [
            { segment: 'marketplace', path: '/marketplace' },
            { segment: pathnames[pathnames.length - 1], path: location.pathname }
        ];
    } else if (pathnames.includes('favorites') && pathnames.includes('details')) {
        // Special handling for favorites details page with correct paths
        breadcrumbs = [
            { segment: 'myaccount', path: '/myaccount', title: 'My Account' },
            { segment: 'Wishlist', path: '/myaccount/favorite', title: 'Wishlist' },
            { segment: 'details', path: location.pathname, title: 'Wishlist Details' }
        ];
    } else {
        // Default behavior for other paths
        breadcrumbs = pathnames.map((segment, index) => {
            const path = `/${pathnames.slice(0, index + 1).join("/")}`;
            const title = getSegmentTitle(segment, path, pathnames);
            return { segment, path, title };
        }).filter(breadcrumb => breadcrumb.title !== null);
    }
    
    return (
        <nav className="text-base font-medium inline-flex items-center">
            <Link to="/" className={`inline-flex gap-1 ${(breadcrumbs.length > 0 && !isHomePage) ? "" : "text-primary" }`}>
                <HomeIcon />
                {isHomePage ? "My Dashboard" : "Home"}
            </Link>
            {breadcrumbs.length > 0 && !isHomePage && (
                <KeyboardArrowRightIcon />
            )}
            {breadcrumbs.map((breadcrumb, index) => (
                !isHomePage ? (
                    <span key={index}>
                        {index !== breadcrumbs.length - 1 ? (
                            <>
                                <Link
                                    to={breadcrumb.path}
                                    className="hover:text-primary"
                                >
                                    {breadcrumb.title || getSegmentTitle(breadcrumb.segment, breadcrumb.path, pathnames)}
                                </Link>
                                <KeyboardArrowRightIcon />
                            </>
                        ) : (
                            <div className="cursor-pointer text-primary">
                                {breadcrumb.title || getSegmentTitle(breadcrumb.segment, breadcrumb.path, pathnames)}
                            </div>
                        )}
                    </span>
                ) : null
            ))}
        </nav>
    );
};

export default Breadcrumbs;